import { useBooleanState } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MembershipInfo } from 'features/user_info';
import { canEditWithoutOLPSelector, eventTypeParticipantsSelector, eventTypeSelector, isCurrentUserEventTypeOwnerSelector, isCurrentUserSelector, isEventTypeOwnerSelector } from '../../selectors';
import { openTransferOwnershipModal, setPermissionRole } from '../../slice';
import { RemoveConfirmationModal } from '../remove_confirmation_modal';
import { FlexContainer, MembershipInfoContainer } from '../styles';
import { RoleSelect } from './role_select';

// 171px is the max width of the RoleSelect component across currently available languages
const ownerLabelWrapperCss = "o1kbejpj";
const OwnerLabelWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(ownerLabelWrapperCss, props.className)} />;
});
const ownerLabelIsPhoneCss = "okd2tex";
const ownerLabelCss = "o13hc56a";
const OwnerLabel = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(ownerLabelCss, {
    [ownerLabelIsPhoneCss]: isPhone
  }, props.className)} />;
});
export const Membership = ({
  membership,
  canRemove,
  onRemove
}) => {
  const dispatch = useDispatch();
  const canEditWithoutOLP = useSelector(canEditWithoutOLPSelector);
  const participants = useSelector(eventTypeParticipantsSelector);
  const participant = participants.find(({
    principleType,
    principleUuid
  }) => principleType === 'User' && principleUuid === membership.user_uuid);
  const isCurrentUserOwner = useSelector(isCurrentUserEventTypeOwnerSelector);
  const isCurrentUser = useSelector(isCurrentUserSelector(participant?.principleUuid));
  const isOwner = useSelector(isEventTypeOwnerSelector(participant?.principleUuid));
  const [isRemoveConfirmationModalOpen, openRemoveConfirmationModal, closeRemoveConfirmationModal] = useBooleanState(false);
  const canTransferOwnership = canEditWithoutOLP || isCurrentUserOwner;
  const {
    name: eventTypeName,
    parent_id: isAdminManaged
  } = useSelector(eventTypeSelector);
  const removeModalTitle = isCurrentUser ? I18n.t('publisher.event_types.edit_hosts_modal.remove_confirmation_modal.title.current_user', {
    event_type_name: eventTypeName
  }) : I18n.t('publisher.event_types.edit_hosts_modal.remove_confirmation_modal.title.member', {
    name: membership.name,
    event_type_name: eventTypeName
  });
  const currentUserText = I18n.t('publisher.event_types.edit_hosts_modal.remove_confirmation_modal.permission_text.current_user');
  const memberText = I18n.t('publisher.event_types.edit_hosts_modal.remove_confirmation_modal.permission_text.member', {
    name: membership.name
  });
  const removeModalText = isCurrentUser ? currentUserText : memberText;
  const handleRemoveConfirmation = () => {
    onRemove();
    closeRemoveConfirmationModal();
  };
  const handleTransferOwnership = () => {
    dispatch(openTransferOwnershipModal(membership));
  };
  const changeRole = value => dispatch(setPermissionRole({
    type: participant.principleType,
    uuid: participant.principleUuid,
    value
  }));
  if (!participant) return null;
  return <>
      <FlexContainer>
        <MembershipInfoContainer>
          <MembershipInfo membership={membership} isAvatarNonInteractive />
        </MembershipInfoContainer>
      </FlexContainer>
      {isOwner ? <OwnerLabelWrapper>
          <OwnerLabel>{I18n.t('publisher.event_types.edit_hosts_modal.owner')}</OwnerLabel>
        </OwnerLabelWrapper> : <RoleSelect value={participant.role} onChange={changeRole} onRemove={canRemove && (() => openRemoveConfirmationModal())} onTransfer={!isAdminManaged && canTransferOwnership && (() => handleTransferOwnership(membership))} />}
      {canRemove && <RemoveConfirmationModal isOpen={isRemoveConfirmationModalOpen} onClose={closeRemoveConfirmationModal} onConfirm={handleRemoveConfirmation} title={removeModalTitle} text={removeModalText} />}
    </>;
};

require("./membership.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./membership.js");